import React, { useRef } from "react";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import mangomint from "@api/mangomint";
import SubmitButton from "@components/ui/submitButton";
import TextInput from "@components/ui/textInput";
import FlowStepContainer from "@components/flows/shared/flowStepContainer";
import FlowStepStartNameContainer from "@components/flows/shared/flowStepStartNameContainer";
import * as classes from "./flowStepUserInfo.module.scss";

export default function FlowStepUserInfo({
  onDataSubmit,
  isDev,
  doApiSignup,
  isMobile,
  stepNo,
  totalSteps,
  isDemo,
  forTrial,
}) {
  const { register, handleSubmit, formState, setError, watch, trigger } = useForm();
  const passRef = useRef();

  const onSubmit = (data) => {
    if (!data["password"]) {
      data["password"] = uuidv4().replace(/-/g, "");
    }
    data["flowId"] = uuidv4().replace(/-/g, "");

    if ((isDev && !doApiSignup) || isDemo) {
      onDataSubmit(data);
      return;
    }

    const apiData = {
      firstName: data["first-name"],
      lastName: data["last-name"],
      email: data["email"],
      password: data["password"],
    };

    mangomint
      .signUpPreflight(apiData)
      .then(() => {
        onDataSubmit(data);
      })
      .catch((e) => {
        if (e.response && e.response.data && e.response.data.errorType === "UserAlreadyExists") {
          setError("email", { type: "UserAlreadyExists", message: "An account with this email already exists." });
        } else {
          alert("An unknown error occurred. Please send us a chat message.");
          console.error(e);
        }
      });
  };

  const { isSubmitting, errors } = formState;

  return (
    <FlowStepContainer
      stepNo={stepNo}
      totalSteps={totalSteps}
      title={isMobile && !isDemo ? "Tell us a little more about yourself." : "What's your name and email?"}
      subTitle={
        isMobile && !isDemo
          ? "We'll set up your account so you can start playing in the Mangomint app, and send you tips to get started (no spam, we promise)."
          : forTrial
          ? "We'll send tips to get you started in Mangomint (no spam, we promise)!"
          : null
      }
    >
      <div className={classes.innerWrap}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlowStepStartNameContainer>
            <TextInput
              tabIndex={1}
              type="text"
              required="required"
              placeholder="Enter here"
              label="First Name"
              errors={errors}
              name="first-name"
              {...register("first-name", {
                required: "Please fill in your first name",
              })}
              v2={true}
            />
            <TextInput
              tabIndex={2}
              type="text"
              name="last-name"
              {...register("last-name", {
                required: "Please fill in your last name",
              })}
              required="required"
              placeholder="Enter here"
              label="Last Name"
              errors={errors}
              v2={true}
            />
          </FlowStepStartNameContainer>

          <TextInput
            tabIndex={3}
            type="email"
            name="email"
            {...register("email", {
              required: "Please fill in your email address",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,5}$/i,
                message: "Invalid email address",
              },
            })}
            onBlur={() => trigger("email")}
            required="required"
            placeholder="Enter your email address"
            label="Email"
            errors={errors}
            v2={true}
          />

          {isMobile && !isDemo && (
            <>
              <TextInput
                tabIndex={4}
                ref={passRef}
                type="password"
                minLength="8"
                {...register("password", {
                  required: "Please fill in your password",
                })}
                onBlur={() => trigger("password")}
                required="required"
                placeholder="Enter a strong password"
                label="Password"
                errors={errors}
                v2={true}
              />
              <TextInput
                tabIndex={5}
                type="password"
                {...register("confirm_password", {
                  required: "Please confirm your password",
                  validate: (val) => {
                    if (watch("password") !== val) {
                      return "Your passwords do no match";
                    }
                  },
                })}
                onBlur={() => trigger("email")}
                required="required"
                placeholder="Confirm your password"
                label="Confirm password"
                errors={errors}
                v2={true}
              />
            </>
          )}
          <div className={classes.btnWrap}>
            <SubmitButton tabIndex={5} disabled={isSubmitting || !formState.isValid}>
              Continue
            </SubmitButton>
          </div>
        </form>
      </div>
    </FlowStepContainer>
  );
}
